import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Copy, Check, Globe } from "lucide-react";
import { CONTACT_EMAIL } from "@/pages/public/Legal";

const SUBJECTS = ["General query", "Calculation issue", "Report issue", "Account / login help", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", subject: "General query", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(CONTACT_EMAIL); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Could not copy"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.message.trim().length < 3) { toast.error("Please enter your name and message."); return; }
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thank you! We will reply soon.");
      setForm({ name: "", mobile: "", email: "", subject: "General query", message: "", company: "" });
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <h1 className="text-3xl md:text-4xl font-heading font-extrabold mb-2">Contact Us</h1>
      <p className="text-thali-ink/70 mb-5">We usually reply within 2–3 working days.</p>

      <div className="card-thali flex items-center justify-between gap-3 mb-5" data-testid="contact-email-card">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-xl bg-thali-green text-white grid place-items-center shrink-0"><Mail className="w-5 h-5" /></span>
          <div className="min-w-0">
            <div className="text-xs text-thali-ink/60">Email us</div>
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-heading font-bold text-thali-green break-all" data-testid="contact-mailto">{CONTACT_EMAIL}</a>
          </div>
        </div>
        <button onClick={copyEmail} className="btn-ghost text-sm px-3 shrink-0" data-testid="contact-copy">
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="card-thali flex items-center justify-between gap-3 mb-5" data-testid="contact-website-card">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-xl bg-thali-green text-white grid place-items-center shrink-0"><Globe className="w-5 h-5" /></span>
          <div className="min-w-0">
            <div className="text-xs text-thali-ink/60">Website</div>
            <a href="https://pmposhanmdmcalculator.in/" target="_blank" rel="noopener noreferrer" className="font-heading font-bold text-thali-green break-all" data-testid="contact-website">pmposhanmdmcalculator.in</a>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-thali space-y-4">
        <div>
          <label className="font-semibold text-sm">Name *</label>
          <input className="input-thali mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-semibold text-sm">Mobile</label>
            <input className="input-thali mt-1" inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="contact-mobile" />
          </div>
          <div>
            <label className="font-semibold text-sm">Email</label>
            <input className="input-thali mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          </div>
        </div>
        <div>
          <label className="font-semibold text-sm">Subject</label>
          <select className="input-thali mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject">
            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="font-semibold text-sm flex justify-between"><span>Message *</span><span className="text-thali-ink/40 font-normal">{form.message.length}/1000</span></label>
          <textarea maxLength={1000} className="input-thali mt-1 min-h-[120px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
        </div>
        <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <button disabled={sending} className="btn-3d-green w-full" data-testid="contact-submit">
          {sending ? "Sending…" : <>Send Message <Send className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}
